import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { PageParams } from 'src/app/core/models/page-params.model';
import { IConstantItem } from '../../../../../core/models/available-types.interface';
import { ICountry } from '../../../../../core/models/country.interface';
import { IPaginatorProperties } from '../../../../../core/models/paginator.interface';
import { NotificationService } from '../../../../../core/services/notification.service';
import { UserService } from '../../../../../core/services/user.service';
import { IFulfillmentShipmentPriceItem, ITypeActivity, ITypeActivityPrice } from '../../../models/shipment-price.interface';
import { ShipmentPriceService } from '../../../services/shipment-price.service';
import { PriceItemsDialogComponent } from '../../shipment-price-items/price-items-dialog/price-items-dialog.component';

@Component({
    selector: 'eci-shipment-price-item-type-activity-prices',
    templateUrl: './item-type-activity-prices.component.html',
    styleUrls: [ './item-type-activity-prices.component.scss' ],
})
export class ItemTypeActivityPricesComponent implements OnInit {
    public form: FormGroup;

    @Input() public shipmentPriceItemId: number;
    @Input() public shipmentPriceItem: IFulfillmentShipmentPriceItem;
    @Input() public typeActivityPrices: ITypeActivityPrice[];
    @Input() public theCountriesList: ICountry[];

    @ViewChild(MatTable) public table: MatTable<ITypeActivityPrice>;

    public currentSort: Sort = {active: 'createdAt', direction: 'desc'};
    public pageParams: PageParams = new PageParams();

    public dataSource: MatTableDataSource<ITypeActivityPrice>;
    public displayedColumns: string[] = [
        'typeName',
        'subTypeName',
        'description',
        'activityUnitName',
        'costRates',
        'margin',
        'sellPrice',
        'currency',
        'maxLength',
        'maxWidth',
        'maxHeight',
        'dimensionsUnitsOfMeasure',
        'maxWeight',
        'weightUnitsOfMeasure',
        'maxVolume',
        'volumeUnitsOfMeasure',
        'action',
    ];
    public showLoader: boolean = false;

    public dimensionsUnits: IConstantItem[] = [
        {id: 0, name: 'mm'},
        {id: 1, name: 'cm'},
        {id: 2, name: 'm'},
        {id: 3, name: 'dm'},
        {id: 4, name: 'km'},
    ];
    public weightUnits: IConstantItem[] = [
        {id: 0, name: 'g'},
        {id: 1, name: 'kg'},
        {id: 2, name: 't'},
    ];
    public volumeUnits: IConstantItem[] = [
        {id: 1, name: 'liters'},
        {id: 2, name: 'mm3'},
        {id: 3, name: 'cm3'},
        {id: 4, name: 'm3'},
    ];
    public typeActivityList: ITypeActivity[] = [];
    public subTypeActivityList: ITypeActivity[] = [];
    public activityUnitList: ITypeActivity[] = [];

    constructor(
        public router: Router,
        private notify: NotificationService,
        private shipmentPriceService: ShipmentPriceService,
        public userSvc: UserService,
        private dialog: MatDialog,
    ) {
    }

    ngOnInit(): void {
        this.dataSource = new MatTableDataSource<ITypeActivityPrice>(this.typeActivityPrices);
        this.pageParams.length = this.dataSource.data.length;

        if (this.shipmentPriceItemId && !this.typeActivityPrices) {
            this.getList();

        }

        this.getActivityList('activity-unit-of-measures', 'activityUnitList', {});
        this.getActivityList('type-activities', 'subTypeActivityList', {isSubType: true});
        this.getActivityList('type-activities', 'typeActivityList', {isSubType: false});
    }

    private getList(page: number = this.pageParams.pageIndex + 1 || 1,
                    limit: number = this.pageParams.pageSize,
                    q: string = this.form?.get('search')?.value || '',
                    orderField: string = this.currentSort?.active || '',
                    orderDir: string = this.currentSort?.direction.toUpperCase() || ''): void {
        this.showLoader = true;
        this.shipmentPriceService.getTypeActivityPricesListByPriceItemId(
            this.shipmentPriceItemId, page, limit, q, {}, orderField, orderDir)
            .subscribe((data) => {
                    if (!data) {
                        return;
                    }
                    this.typeActivityPrices = data.items;
                    this.dataSource = new MatTableDataSource<ITypeActivityPrice>(data.items);
                    this.pageParams.pageSize = data.meta.itemsPerPage;
                    this.pageParams.pageIndex = data.meta.currentPage - 1;
                    this.pageParams.length = data.meta.totalItems;
                }, (err) => {
                    this.notify.showError(err.error.message);
                }, () => {
                    this.showLoader = false;
                });
    }

    private getModel(data: { form: any; element: ITypeActivityPrice }): ITypeActivityPrice {
        const currentData = data.element;
        currentData.shipmentPriceItemId = this.shipmentPriceItemId;
        currentData.typeName = data.form.typeName;
        currentData.subTypeName = data.form.subTypeName;
        currentData.typeId = data.form.subTypeName;
        currentData.activityUnitId = data.form.activityUnitName;
        currentData.activityUnitName = data.form.activityUnitName;
        currentData.description = data.form.description;
        currentData.costRates = data.form.costRates;
        currentData.margin = data.form.margin;
        currentData.sellPrice = data.form.sellPrice;
        currentData.currency = data.form.currency;
        currentData.maxWidth = data.form.maxWidth;
        currentData.maxHeight = data.form.maxHeight;
        currentData.maxLength = data.form.maxLength;
        currentData.dimensionsUnitsOfMeasure = data.form.dimensionsUnitsOfMeasure;
        currentData.maxWeight = data.form.maxWeight;
        currentData.weightUnitsOfMeasure = data.form.weightUnitsOfMeasure;
        currentData.maxVolume = data.form.maxVolume;
        currentData.volumeUnitsOfMeasure = data.form.volumeUnitsOfMeasure;
        return currentData;
    }

    private onResponseSuccess(element: ITypeActivityPrice, onSuccess: any, index: number): void {
        this.showLoader = false;
        onSuccess();
        this.typeActivityPrices[index] = element;
        this.dataSource.data = this.typeActivityPrices;
    }

    public pageChanged(e: PageParams): void {
        this.pageParams.pageIndex = e.pageIndex;
        this.pageParams.pageSize = e.pageSize;
        this.getList();
    }

    public sortData(sort: Sort): void {
        const data = this.dataSource;
        if (!sort.active || sort.direction === '') {
            this.dataSource = data;
            return;
        }
        this.currentSort = sort;
        this.getList();
    }

    public get isAdmin(): boolean {
        return this.userSvc.isAdmin;
    }

    public addNewRow(event): void {
        if (!this.dataSource?.data) {
            this.dataSource = new MatTableDataSource<ITypeActivityPrice>([ event ]);
            this.shipmentPriceItemId = event.shipmentPriceItemId;
        }

        this.typeActivityPrices.push(event);
        this.dataSource.data = this.typeActivityPrices;
    }

    public removeData(index: number): void {
        this.typeActivityPrices.splice(index, 1);
        this.dataSource.data = this.typeActivityPrices;
    }

    public save(data: { form: any; element: ITypeActivityPrice, onSuccess: any, index: number }): void {
        if (data.element.id) {
            this.update(data);
            return;
        }
        this.showLoader = true;
        this.shipmentPriceService.create<ITypeActivityPrice>('type-activity-prices', this.getModel(data))
            .subscribe((p) => {
                this.notify.showSuccessMessage(`Created successfully`);
                this.onResponseSuccess(p, data.onSuccess, data.index);
            }, (err) => {
                this.notify.showError(err.error.message);
                this.showLoader = false;
            });
    }

    public update(data: { form: FormGroup; element: ITypeActivityPrice; onSuccess: any; index: number }): void {
        this.showLoader = true;
        this.shipmentPriceService.update<ITypeActivityPrice>('type-activity-prices', data.element.id, this.getModel(data))
            .subscribe((p) => {
                this.notify.showSuccessMessage(`Updated successfully`);
                this.onResponseSuccess(p, data.onSuccess, data.index);
            }, (err) => {
                this.notify.showError(err.error.message);
                this.showLoader = false;
            });
    }

    public delete(element: { id: number; index: number }): void {
        this.showLoader = true;
        this.shipmentPriceService.delete('type-activity-prices', element.id)
            .subscribe((p) => {
                this.notify.showSuccessMessage(`Deleted successfully`);
                this.removeData(element.index);
            }, (err) => {
                this.notify.showError(err.error.message);
                this.showLoader = false;
            });
    }

    public openUploadDialog(): void {
        const dialogRef = this.dialog.open(PriceItemsDialogComponent,
            {
                maxHeight: '800px',
                width: '900px',
            },
        );
        dialogRef.componentInstance.createMode = false;
        dialogRef.componentInstance.shipmentPrice = this.shipmentPriceItem;
        dialogRef.componentInstance.vendors = [ this.shipmentPriceItem.vendor ];
        dialogRef.componentInstance.services = [ this.shipmentPriceItem.shipmentService ];
        dialogRef.componentInstance.subServices = [ this.shipmentPriceItem.subService ];
        dialogRef.componentInstance.isFulfilment = true;
        dialogRef.componentInstance.serviceProvider = [this.shipmentPriceItem.serviceProvider];
        dialogRef.componentInstance.title = 'Price Upload';
        dialogRef.afterClosed()
            .subscribe((result) => {
                this.getList();
            });
    }

    private getActivityList(path: string, listName: string , filter = {}, page: number = 1, limit: number = 1000, search: string = undefined): void {
        this.shipmentPriceService.get<IPaginatorProperties<ITypeActivity>>(path,
            page,
            limit,
            search,
            filter)
            .subscribe((data) => {
                if (!data) {
                    return;
                }
                this[listName] = data?.items;
            });
    }

}
